'use client';
import * as Dialog from '@radix-ui/react-dialog';
import { ReactNode } from 'react';
import { CiCircleRemove } from 'react-icons/ci';
import { Input } from '../ui/input';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useRouter } from 'next/navigation';
import { fetchAvailableFiltersRequest } from '@/utils/filter-request';
import { useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { Combobox } from '@/components/utils/combobox';
import { FildeSearchMorCom } from '..';
import { TypeFileDsearchGetAllData } from './home/types/home.type';
import { formatNameDoctor } from '@/constant/scripts';
import { IoLocationSharp } from 'react-icons/io5';
import { Loader } from 'lucide-react';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';
import Image from 'next/image';
import { FaAngleLeft, FaSearch } from 'react-icons/fa';

interface ModalProps {
    buttonElement?: ReactNode;
    hideCity?: boolean;
    city?: string;
    onSelectCity?: (city?: string) => void;
    hideSearchIcon?: boolean;
    inputClassName?: string;
    placeHolder?: string;
}

const SearchModal = (props: ModalProps) => {
    const { buttonElement, hideCity, city, onSelectCity } = props;
    const router = useRouter();
    const { data: filters } = useQuery({
        queryKey: ['search', 'filters'],
        queryFn: fetchAvailableFiltersRequest,
    });

    const [searchResultOpen, setSearchResultOpen] = useState(false);

    const [text, setText] = useState('');
    const [debouncedValue] = useDebounce(text, 500);

    const filteredServices = useMemo(() => {
        if (!filters) return [];

        return filters.service
            .filter((i) =>
                i.label.toLowerCase().includes(debouncedValue.toLowerCase()),
            )
            .slice(0, 5);
    }, [debouncedValue, filters]);

    const filteredSpecialties = useMemo(() => {
        if (!filters) return [];

        return filters.specialty
            .filter((i) =>
                i.label.toLowerCase().includes(debouncedValue.toLowerCase()),
            )
            .slice(0, 5);
    }, [debouncedValue, filters]);

    const {
        data: dataValue,
        isPending,
        isError,
    } = useQuery({
        queryKey: ['search', debouncedValue],
        queryFn: async () => {
            if (debouncedValue.trim() === '') {
                return { docs: [], special: [], docs_count: [], specialty: [] };
            }
            try {
                const url = `${
                    process.env.NEXT_PUBLIC_STATIC_URL
                }/api/main_search?word=${encodeURIComponent(debouncedValue)}`;

                const response = await axios.get(url);
                return response.data.result;
            } catch {
                throw new Error('Error fetching data');
            }
        },
    });

    const defaultOnSelectCity = (city?: string) => {
        if (city) {
            router.push(`/doctors/city/${city}`);
        } else {
            router.push('/doctors');
        }
    };

    return (
        <Dialog.Root
            open={searchResultOpen}
            onOpenChange={(o) => {
                setText('');
                setSearchResultOpen(o);
            }}
        >
            <Dialog.Trigger asChild>
                {buttonElement ? (
                    buttonElement
                ) : (
                    <div className="flex gap-2 relative items-center cursor-pointer w-full">
                        <Input
                            onChange={() => {}}
                            value={text}
                            type="text"
                            id="search-input"
                            autoComplete="off"
                            placeholder={
                                props.placeHolder
                                    ? props.placeHolder
                                    : 'نام متخصص، مرکز درمانی ...'
                            }
                            className={`${
                                props.inputClassName ? props.inputClassName : ''
                            } border !bg-white text-black border-neutral-200 outline-none !ring-0 placeholder:!text-[#999999] !rounded-2xl !px-3 py-2.5`}
                        />
                        {!props.hideSearchIcon ? (
                            <Button
                                variant="outline"
                                aria-label="جستجو"
                                name="search"
                                className={` flex !h-[36.8px] !border-0 !px-2.5 absolute left-[3px] hover:!opacity-80 !transition-opacity top-[0.5] w-max !bg-white justify-between text-xs flex-grow`}
                            >
                                <FaSearch className="text-xl text-blue" />
                            </Button>
                        ) : (
                            ''
                        )}
                    </div>
                )}
            </Dialog.Trigger>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 bg-[#0000002b] opacity-100" />
                <Dialog.Content className="flex_col justify-center items-center gap-3 !rounded-2xl data-[state=open]:animate-contentShow fixed !pb-3 !pt-[30px] top-[50%] left-[50%] max-h-[85vh] w-[90vw] max-w-[600px] translate-x-[-50%] translate-y-[-50%] bg-white px-5 shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] focus:outline-none">
                    <Dialog.Title className="text-2xl text-right font-bold w-full !pt-0">
                        جستجو
                    </Dialog.Title>
                    <div className="flex gap-2 items-center w-full">
                        <Input
                            onChange={(e) => {
                                setText(e.target.value);
                            }}
                            value={text}
                            type="text"
                            placeholder="نام متخصص، مرکز درمانی ..."
                            className="border !bg-white text-black border-neutral-200 placeholder:!text-[#999999] !outline-none !ring-0 w-full !rounded-2xl !px-3 py-2.5"
                        />
                        {!hideCity && filters && (
                            <div className="flex-shrink w-fit">
                                <Combobox
                                    placeholder="شهر"
                                    onChange={(selectedItem) => {
                                        (onSelectCity ?? defaultOnSelectCity)(
                                            selectedItem,
                                        );
                                    }}
                                    options={filters.city}
                                    btnClassName="!text-white !bg-blue !h-[41.5px]"
                                    value={city}
                                    reverseColoring
                                />
                            </div>
                        )}
                    </div>

                    <div className="overflow-y-auto flex-grow w-full custom_scroll_bar">
                        <div
                            className={cn(
                                'flex flex-col gap-2 text-black custom-scrollbar z-40',
                                (isPending ||
                                    isError ||
                                    (dataValue.docs &&
                                        dataValue.docs.length === 0 &&
                                        debouncedValue.trim().length > 0 &&
                                        filteredServices.length === 0 &&
                                        filteredSpecialties.length === 0)) &&
                                    'min-w-full min-h-full',
                            )}
                        >
                            {debouncedValue.trim().length <= 0 ? null : (
                                <>
                                    {isPending ? (
                                        <div className="flex items-center justify-center w-full flex-grow">
                                            <Loader className="text-blue w-7 h-7 animate-spin duration-1000" />
                                        </div>
                                    ) : isError ? (
                                        <p className="text-center">
                                            خطا در دریافت داده‌ها
                                        </p>
                                    ) : dataValue &&
                                      dataValue.docs &&
                                      dataValue.docs.length > 0 ? (
                                        <div>
                                            {dataValue.docs.map(
                                                (
                                                    i: TypeFileDsearchGetAllData,
                                                    index: number,
                                                ) => {
                                                    return (
                                                        <Link
                                                            key={i.id}
                                                            href={`/doctor/${
                                                                i.id
                                                            }/${formatNameDoctor(
                                                                i.name,
                                                            )}`}
                                                            className=""
                                                        >
                                                            <div
                                                                className={cn(
                                                                    'flex justify-between pl-1 items-center pr-3 gap-4 py-3 text_sm border-gray-300 transition-colors hover:bg-gray-200 ',
                                                                    index <
                                                                        dataValue
                                                                            .docs
                                                                            .length -
                                                                            1 &&
                                                                        'border-b',
                                                                )}
                                                            >
                                                                <div className="flex gap-3 items-center w-full">
                                                                    <Image
                                                                        className="max-w-[60px] max-h-[60px] w-full h-full rounded-2xl"
                                                                        src={
                                                                            process
                                                                                .env
                                                                                .NEXT_PUBLIC_STATIC_URL +
                                                                            i.pic
                                                                        }
                                                                        alt={
                                                                            i.name
                                                                        }
                                                                        width={
                                                                            1000
                                                                        }
                                                                        height={
                                                                            1000
                                                                        }
                                                                    />
                                                                    <div>
                                                                        <h2 className="text-sm font-semibold">
                                                                            {
                                                                                i.name
                                                                            }
                                                                        </h2>
                                                                        <h2 className="text-xs font-regular pt-1">
                                                                            {
                                                                                i.specialty
                                                                            }
                                                                        </h2>
                                                                    </div>
                                                                </div>
                                                                <div className="flex w-max pl-3 text-blue flex-shrink justify-between">
                                                                    <h2 className="text-xs flex items-center">
                                                                        <IoLocationSharp className="i_f" />
                                                                        {
                                                                            i.state
                                                                        }
                                                                    </h2>
                                                                </div>
                                                            </div>
                                                        </Link>
                                                    );
                                                },
                                            )}
                                            <Link
                                                className="bg-blue px-4 py-2 rounded-2xl w-max text-white mx-auto mt-3 gap-1 text-sm flex items-center justify-center"
                                                href={`/doctors?name=${debouncedValue.trim()}`}
                                            >
                                                مشاهده بیشتر
                                                <FaAngleLeft />
                                            </Link>
                                        </div>
                                    ) : (
                                        <div className="p-1 flex items-center justify-center flex-grow">
                                            موردی یافت نشد
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {!text &&
                            (filteredServices.length > 0 ||
                                filteredSpecialties.length > 0) && (
                                <>
                                    {filteredServices.length > 0 && (
                                        <h2 className="text-center font-semibold pt-2 my-1">
                                            جستجو بر اساس خدمات
                                        </h2>
                                    )}
                                    {filteredServices.map((i) => (
                                        <FildeSearchMorCom
                                            key={i.api}
                                            id={parseInt(i.api)}
                                            name={i.label}
                                            slug={i.value}
                                            type="service"
                                            prefix="خدمت"
                                        />
                                    ))}

                                    {filteredSpecialties.length > 0 && (
                                        <h2 className="text-center font-semibold pt-4 my-1">
                                            جستجو بر اساس تخصص
                                        </h2>
                                    )}
                                    {filteredSpecialties.map((i) => (
                                        <FildeSearchMorCom
                                            key={i.api}
                                            id={parseInt(i.api)}
                                            name={i.label}
                                            slug={i.value}
                                            type="specialty"
                                            prefix="دکتر"
                                        />
                                    ))}
                                </>
                            )}
                    </div>
                    <Dialog.Close asChild>
                        <button
                            className="font-bold text-2xl hover:bg-violet4 focus:shadow-violet7 absolute top-[10px] left-[10px] inline-flex h-[25px] w-[25px] appearance-none items-center justify-center rounded-full focus:shadow-[0_0_0_2px] focus:outline-none"
                            aria-label="Close"
                        >
                            <CiCircleRemove />
                        </button>
                    </Dialog.Close>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

export default SearchModal;
